import { Handset, Article as ArticleType, HandsetDeal } from "@/types/index";
import Head from "next/head";
import * as React from "react";
import { useRouter } from "next/router";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getOne } from "@/api/contentful";
import Article from "@/components/Article";
import { createDealClick } from "@/api/tracking";
import PageSEO from "@/components/Shared/PageSEO";
import { UtmContext } from "@/context/utmContext";
import { KeywordContext } from "@/context/keyword";
import Sorter from "@/components/Comparison/Sorter";
import Filter from "@/components/Comparison/Filter";
import Loading from "@/components/Comparison/Loading";
import LoadMore from "@/components/Comparison/LoadMore";
import { getHandsets, getPhoneSpec } from "@/api/products";
import useMobileComparison from "@/hooks/useMobileComparison";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Layout from "@/components/Comparison/Layouts/MobileLayout";
import HeaderContent from "@/components/Comparison/HeaderContent";
import MobileDeal from "@/components/Comparison/Deals/MobileDeal";
import DealsNotFound from "@/components/Comparison/DealsNotFound";
import { HeaderContent as HeaderContentType } from "@/types/header";
import MobileFilter from "@/components/Comparison/Filter/MobileFilter";
import { getArticleForHandset } from "@/utils/getArticleForMobileHandset";
import HeaderPhoneSelector from "@/components/Header/HeaderPhoneSelector";
import { slugToFriendlyMobileName } from "@/utils/slugToFriendlyMobileName";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Mobile",
		href: "/mobile",
		active: false,
	},
];

const ManufacturerHandset: NextPage<{
	article: ArticleType;
	headerContent: HeaderContentType;
	routerParams: {
		manufacturer: string;
		name: string;
		model: string;
	};
	handsetOptions: {
		data: {
			colours: Array<string>;
			storage: Array<string>;
		};
	};
	friendlyName: string;
}> = ({
	headerContent,
	article,
	routerParams,
	handsetOptions,
	friendlyName,
}) => {
	const router = useRouter();
	const { utmContent, utmCampaign, utmMedium, utmSource, utmTerm } =
		React.useContext(UtmContext);
	const { sclid, gclid, fbclid, msclkid, tbclid, obclid } =
		React.useContext(KeywordContext);

	const mobileComparison = useMobileComparison(
		routerParams.manufacturer?.toString().replace(/-/g, " ") || "",
		routerParams.name?.toString().replace(/-/g, " ") || "",
		routerParams.model?.toString().replace(/-/g, " ") || ""
	);

	const incrementViewableDeals = () => {
		mobileComparison.setPage(mobileComparison.page + 1);
	};

	const deals = mobileComparison.products.map(function (deal, index) {
		const onDealClick = async (product: HandsetDeal) => {
			createDealClick({
				type: "handsets",
				click_reference:
					sclid ?? gclid ?? fbclid ?? msclkid ?? tbclid ?? obclid ?? utmContent,
				utm_campaign: utmCampaign,
				utm_content: utmContent,
				utm_source: utmSource,
				utm_medium: utmMedium,
				utm_term: utmTerm,
				product: product.handset.name,
				provider: product.network,
				metadata: {
					dealPosition: index + 1,
					page: router.isReady ? router.asPath : "",
					monthlyCost: product.monthly_price,
					handsetPrice: product.handset_price,
					upfrontCost: product.upfront_price,
					contractLength: product.contract_length,
					dataAllowance: product.data_allowance,
					minutesAllowance: product.minutes_allowance,
					textsAllowance: product.texts_allowance,
				},
			});
		};

		return (
			<div className="col-12 text-start" key={index}>
				<MobileDeal
					data={deal}
					urlReference={
						sclid ??
						gclid ??
						fbclid ??
						msclkid ??
						tbclid ??
						obclid ??
						utmContent
					}
					onDealClick={onDealClick}
				/>
			</div>
		);
	});

	const filterProps = [
		{
			title: "Monthly Cost",
			filters: mobileComparison.monthlyFilters,
			toggleFilter: mobileComparison.toggleMonthlyFilter,
		},
		{
			title: "Contract Length",
			filters: mobileComparison.contractFilters,
			toggleFilter: mobileComparison.toggleContractFilter,
		},
		{
			title: "Data",
			filters: mobileComparison.dataFilters,
			toggleFilter: mobileComparison.toggleDataFilter,
		},
		{
			title: "Network",
			filters: mobileComparison.networkFilters,
			toggleFilter: mobileComparison.toggleNetworkFilter,
		},
		{
			title: "Upfront Cost",
			filters: mobileComparison.upFrontCostFilters,
			toggleFilter: mobileComparison.toggleUpFrontCostFilter,
		},
		{
			title: "Minutes",
			filters: mobileComparison.minutesFilters,
			toggleFilter: mobileComparison.toggleMinutesFilter,
		},
	];

	return (
		<div className="h-100 d-flex flex-column">
			{article.fields.slug === "about-mobile-deals" ? (
				<Head>
					<link rel="canonical" href="https://www.savely.co.uk/mobile" />
				</Head>
			) : (
				<Head>
					<link
						rel="canonical"
						href={`https://www.savely.co.uk/mobile/${routerParams.manufacturer}/${routerParams.name}/${routerParams.model}`}
					/>
				</Head>
			)}
			<PageSEO
				title={`Compare ${friendlyName} Mobile Deals | Consumer Choices`}
				description={`Looking for an amazing deal on ${friendlyName} mobile phones? We will help you compare prices and find unbeatable offers. Shop now and save on your next smartphone purchase.`}
				keywords={`${friendlyName} Mobile Deals`}
			/>
			<Header
				left={
					<HeaderPhoneSelector
						heading={"Choose yours"}
						colours={handsetOptions.data.colours}
						storage={handsetOptions.data.storage}
						setStorage={mobileComparison.setSelectedStorage}
						setColour={mobileComparison.setSelectedColour}
					/>
				}
				theme={{
					paddingTop: 5,
					paddingRight: 3,
					paddingLeft: 3,
					paddingBottom: 5,
				}}
				right={<HeaderContent data={headerContent} />}
				breadcrumbs={[
					...Breadcrumbs,
					{
						title: friendlyName,
						href: `/mobile/${routerParams.manufacturer}/${routerParams.name}/${routerParams.model}`,
						active: true,
					},
				]}
			/>
			<div id="deals">
				<Layout
					desktopFilters={<Filter filterProps={filterProps} />}
					mobileFilters={<MobileFilter filterProps={filterProps} />}
					comparisonTable={
						mobileComparison.loading ? (
							<Loading />
						) : deals.length > 0 ? (
							deals
						) : (
							<DealsNotFound clearFilters={mobileComparison.clearFilters} />
						)
					}
					loadMore={
						deals.length > 0 &&
						mobileComparison.hasNextPage &&
						!mobileComparison.loading ? (
							<LoadMore onClick={incrementViewableDeals} />
						) : (
							<></>
						)
					}
					sorter={
						<Sorter
							defaultSort={"savely_recommended"}
							sortPropsVariant={"mobile"}
							setSorter={mobileComparison.setSorter}
						/>
					}
				/>
			</div>
			<Article article={article} showAuthor={false} showSideBar={false} />
			<Footer />
		</div>
	);
};

export const getStaticPaths: GetStaticPaths<{
	manufacturer: string;
	name: string;
	model: string;
}> = async () => {
	// Query for all available handsets.
	const handsets = await getHandsets();

	if (handsets.data === null) {
		return {
			paths: [],
			fallback: false,
		};
	}

	// Map the result of that query to a list of handsets.
	const paths = handsets.data.map(
		({ manufacturer, product_name: name, model }: Handset) => ({
			params: {
				manufacturer: manufacturer.replace(/\s+/g, "-").toLowerCase(),
				name: name.replace(/\s+/g, "-").toLowerCase(),
				model: model.replace(/\s+/g, "-").toLowerCase(),
			},
		})
	);

	return {
		paths,
		fallback: false,
	};
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
	if (
		typeof params?.manufacturer !== "string" ||
		typeof params?.name !== "string" ||
		typeof params?.model !== "string"
	) {
		return { props: {} };
	}

	const friendlyName = slugToFriendlyMobileName(
		params.manufacturer,
		params.name,
		params.model
	);

	const handsetOptions = await getPhoneSpec(
		params?.name.replace(/-/g, " ") ?? "",
		params?.model.replace(/-/g, " ") ?? ""
	);

	const contentParameters = getArticleForHandset(
		params.manufacturer,
		params.name,
		params.model
	);

	const headerContent = await getOne(contentParameters.header);
	const article = await getOne(contentParameters.article);

	return {
		props: {
			headerContent,
			article,
			routerParams: params,
			handsetOptions,
			friendlyName,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
};

export default ManufacturerHandset;
